import { isObject } from 'lodash-es';
import type { BlotterTableState } from '../../components/BlotterTable/BlotterTableStorageContext';
import { blotterColumnStateToGridState } from '../../components/BlotterTable/blotterTableGridState';
import type { WLCustomerUserConfigAllTime } from '../../types/WLCustomerUserConfig';
import type { WLConfigMigrationResult } from './types';

const LEGACY_BLOTTER_PREFIX = 'blotters.';
const BLOTTERS3_PREFIX = 'blotters3.';
type LegacyBlotterState = NonNullable<WLCustomerUserConfigAllTime['blotters']>[string];

/**
 * Migrate legacy WL persisted stringified blotter states (`blotters.<id>`) into `blotters3.<id>`.
 * Existing `blotters3.<id>` values are preserved.
 */
export function migrateBlottersToBlotters3(config: Partial<WLCustomerUserConfigAllTime>): WLConfigMigrationResult {
  if (hasBlotters3State(config)) {
    // If a blotters3-prefixed state already exists, it means we have already migrated, so do nothing.
    return { config, diff: {} };
  }

  const legacyEntries: Array<[string, LegacyBlotterState]> = [];
  for (const [key, value] of Object.entries(config)) {
    if (!key.startsWith(LEGACY_BLOTTER_PREFIX) || key.length <= LEGACY_BLOTTER_PREFIX.length || value == null) {
      continue;
    }

    legacyEntries.push([key.slice(LEGACY_BLOTTER_PREFIX.length), value as LegacyBlotterState]);
  }

  if (legacyEntries.length === 0) {
    // Nothing to migrate, do nothing.
    return { config, diff: {} };
  }

  const blotters3KeyedStates: Partial<WLCustomerUserConfigAllTime> = {};
  for (const [blotterId, blotterState] of legacyEntries) {
    const migratedBlotterState = mapLegacyBlotterStateToBlotter3State(blotterState);
    if (migratedBlotterState != null) {
      blotters3KeyedStates[`${BLOTTERS3_PREFIX}${blotterId}`] = migratedBlotterState;
    }
  }

  if (Object.keys(blotters3KeyedStates).length === 0) {
    return { config, diff: {} };
  }

  return {
    config: { ...config, ...blotters3KeyedStates },
    diff: blotters3KeyedStates,
  };
}

function hasBlotters3State(config: Partial<WLCustomerUserConfigAllTime>) {
  for (const [key, value] of Object.entries(config)) {
    if (key.startsWith(BLOTTERS3_PREFIX) && key.length > BLOTTERS3_PREFIX.length && value != null) {
      return true;
    }
  }

  return false;
}

function mapLegacyBlotterStateToBlotter3State(legacyBlotterState: LegacyBlotterState): BlotterTableState | undefined {
  if (legacyBlotterState == null || typeof legacyBlotterState !== 'object') {
    return undefined;
  }

  const blotter3State: BlotterTableState = {};

  if ('columns' in legacyBlotterState && Array.isArray(legacyBlotterState.columns)) {
    // Handle old v2 state model where column state was persisted directly.
    blotter3State.gridState = blotterColumnStateToGridState(legacyBlotterState.columns);
  }

  if ('filter' in legacyBlotterState && isObject(legacyBlotterState.filter)) {
    blotter3State.filter = legacyBlotterState.filter;
  }

  if (Object.keys(blotter3State).length === 0) {
    // If the legacy blotter entry (eg trades) has nothing in it, we just drop it. We don't need empty blotter states in blotters3.
    return undefined;
  }

  return blotter3State;
}
